import { isURL } from 'validator'

import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { VoiceParams } from 'hooks/useBrandVoices'

import { AiClient, AiMalformedResponseError, GptPayload } from './AiClient'

export class WebsiteScrapingError extends Error {
  shouldRetry = false
  errorCode?: string
  constructor({ cause, errorCode }: { cause?: object; errorCode?: string }) {
    super('Error scraping website')
    this.name = 'WebsiteScrapingError'
    this.cause = cause
    this.errorCode = errorCode
  }
}

export class AiBrandVoiceDetectionError extends Error {
  shouldRetry = false
  errorCode: string
  assessment?: string
  constructor({ errorCode, assessment }: { errorCode: string; assessment?: string }) {
    super('AI called an error function')
    this.name = 'AiErrorFunctionCall'
    this.errorCode = errorCode
    this.assessment = assessment
  }
}

export const gpt4Payload = (sampleText: string): GptPayload => {
  return {
    model: 'gpt-4o',
    temperature: 0.7,
    messages: [
      {
        role: 'system',
        content:
          `Your task is to find out what "brand voice" was used to create a document. ` +
          `The document comes from a crawled web page/content which the user has access to\n` +
          `You will receive a document that exemplifies a content piece written in said brand voice,` +
          ` and must evaluate the document in the following categories:\n` +
          `1. Purpose: e.g. ["Connect with users as a friend", "Educate an inexperienced audience"]\n` +
          `2. Audience: e.g. ["Product managers"]\n` +
          `3. Tone: e.g. ["Casual", "Conversational", "Relatable"]\n` +
          `4. Emotions: e.g. ["Positive", "Optimistic", "Confident", "Assertive"]\n` +
          `5. Character: e.g. ["Relatable", "Expert", "Trusted advisor"]\n` +
          `6. Syntax / sentence structure: e.g. ["Use contractions", "Provide plenty of examples"]\n` +
          `7. Language: e.g. ["Simple", "Informal", "Playful", "Hip"]\n` +
          `8. Name: give it a unique but memorable name, e.g. "Candyland Voice"\n` +
          `If you think the content is not suitable for import, DO NOT REPLY (I will interpret replies as the brand voice of the content and will be confused), and instead call the \`notifyError\` function to let us know.\n` +
          `Only invoke the notifyError function to inform us if you have not built the JSON object we requested. Do not invoke it if you have build the requested voice.\n` +
          `Do not concern yourself with the content of the document. Do not consider phrases that look like error messages or privacy/cookie policies for the tone. Focus only on finding out what the brand voice is for that document.\n` +
          `Phrase it in such a way that the voice could be used to create further documents ` +
          `(phrase it as instructions, not descriptions, and avoid the past tense)\n` +
          `Use only the following format in your reply:\n` +
          JSON.stringify({
            purpose: [],
            audience: [],
            tone: [],
            emotions: [],
            character: [],
            syntax: [],
            language: [],
            name: '',
          }),
      },
      {
        role: 'user',
        content: sampleText,
      },
      {
        role: 'user',
        content:
          `What are the brand purpose, audience, tone, emotions, character, syntax, and language of the provided text? ` +
          `Be thorough and specific; this will serve as a template to guide users. Without adequate detail, they might produce generic content. ` +
          `Always phrase your findings as instructions, not mere descriptions.\n` +
          `Remember:\nIt's acceptable and even encouraged to provide multiple values inside each category. For example:\n` +
          `"syntax": ["Use complete sentences", "Make heavy use of parentheses and aside clauses", "Use contractions", "Use the active voice"]\n` +
          `"language": ["Simple", "Informal", "Playful", "Hip", "Incorporate memes and pop culture references", "Employ emojis, but sparingly"]\n` +
          `While entries can include commas, don't lump distinct items into a single string. For instance, instead of:\n` +
          `"emotions": "Confidence, especially within the industry, Trust, Authority"\n` +
          `use:\n` +
          `"emotions": ["Confidence, especially within the industry", "Trust", "Authority"]\n`,
      },
    ],
    functions: [
      {
        name: 'notifyError',
        description:
          'Call this function when you suspect that the crawler couldn\'t fetch the page the user intended. This happens when, instead of the page the user wanted to import, our crawler instead received some other page that is probably not the one the user had in mind. For example, the user will try to import a page containing a blog post or a short story, but our crawler instead receives a page containing a login form, a "verify you are not a robot" page, an error message, etc.',
        parameters: {
          type: 'object',
          properties: {
            errorCode: {
              type: 'string',
              description: 'The category of error that occurred, if that can be determined.\n',
              enum: [
                'gibberish',
                'serverError',
                'loginPage',
                'blockedMessage',
                'robotCheck',
                'emptyPage',
                'paymentWall',
                'other',
              ],
            },
            assessment: {
              type: 'string',
              description:
                '(optional) Do you have a message for the user or admin? This is probably not needed if you are using an `errorCode`.\n',
            },
          },
          required: ['errorCode'],
        },
      },
    ],
  }
}

export const gpt3Payload = (sampleText: string): GptPayload => {
  return {
    model: 'gpt-3.5-turbo-16k',
    temperature: 0.7,
    messages: [
      {
        role: 'system',
        content:
          `Your task is to find out what "brand voice" was used to create a document. ` +
          `The document comes from a crawled web page/content which the user has access to\n` +
          `You will receive a document that exemplifies a content piece written in said brand voice,` +
          ` and must evaluate the document in the following categories:\n` +
          `1. Purpose: e.g. ["Connect with users as a friend", "Educate an inexperienced audience"]\n` +
          `2. Audience: e.g. ["Product managers"]\n` +
          `3. Tone: e.g. ["Casual", "Conversational", "Relatable"]\n` +
          `4. Emotions: e.g. ["Positive", "Optimistic", "Confident", "Assertive"]\n` +
          `5. Character: e.g. ["Relatable", "Expert", "Trusted advisor"]\n` +
          `6. Syntax / sentence structure: e.g. ["Use contractions", "Provide plenty of examples"]\n` +
          `7. Language: e.g. ["Simple", "Informal", "Playful", "Hip"]\n` +
          `8. Name: give it a unique but memorable name, e.g. "Candyland Voice"\n` +
          `Do not concern yourself with the content of the document. Do not consider phrases that look like error messages or privacy/cookie policies for the tone. Focus only on finding out what the brand voice is for that document.\n` +
          `Phrase it in such a way that the voice could be used to create further documents ` +
          `(phrase it as instructions, not descriptions, and avoid the past tense)\n` +
          `Use only the following format in your reply:\n` +
          JSON.stringify({
            purpose: [],
            audience: [],
            tone: [],
            emotions: [],
            character: [],
            syntax: [],
            language: [],
            name: '',
          }),
      },
      {
        role: 'user',
        content: sampleText,
      },
      {
        role: 'user',
        content:
          `What are the brand purpose, audience, tone, emotions, character, syntax, and language of the provided text? ` +
          `Be thorough and specific; this will serve as a template to guide users. Without adequate detail, they might produce generic content. ` +
          `Always phrase your findings as instructions, not mere descriptions.\n` +
          `Remember:\nIt's acceptable and even encouraged to provide multiple values inside each category. For example:\n` +
          `"syntax": ["Use complete sentences", "Make heavy use of parentheses and aside clauses", "Use contractions", "Use the active voice"]\n` +
          `"language": ["Simple", "Informal", "Playful", "Hip", "Incorporate memes and pop culture references", "Employ emojis, but sparingly"]\n` +
          `While entries can include commas, don't lump distinct items into a single string. For instance, instead of:\n` +
          `"emotions": "Confidence, especially within the industry, Trust, Authority"\n` +
          `use:\n` +
          `"emotions": ["Confidence, especially within the industry", "Trust", "Authority"]\n`,
      },
    ],
  }
}

export const BrandVoiceDetectionClient = {
  async scanWebsite(website: string, signal?: any, options?: { onAbort: () => void }) {
    let url = website
    if (
      !isURL(url, { require_tld: true, require_protocol: true }) &&
      isURL(`https://${url}`, { require_tld: true, require_protocol: true })
    ) {
      // if we can turn `sample` into a valid URL by prepending `https://`, do so
      url = `https://${url}`
    }

    const browserlessResponse = await sendToApi(`/scrape_url`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    })

    if (browserlessResponse.type === 'exception' && browserlessResponse.data.name === 'AbortError') {
      options?.onAbort()
      return
    }

    if (browserlessResponse.type !== SUCCESS_RESPONSE_TYPE || browserlessResponse.data.length === 0) {
      throw new WebsiteScrapingError({ cause: { url } })
    }

    return this.scanText(browserlessResponse.data, signal)
  },
  async scanText(text: string, signal?: any) {
    // a token is, using a very crude approximation, equivalent to 4 English characters
    // and GPT4 has a max context length of 8k tokens so we limit the
    // sample to 5k "tokens", leaving 2k for the prompt and 1k of leeway
    const sampleText = text.slice(0, 5 * 1024 * 4)
    const gpt4response = await AiClient.chat(gpt4Payload(sampleText), signal, {
      fallbackPayloads: [gpt3Payload(sampleText)],
    })

    if (gpt4response.choices[0].finish_reason === 'function_call') {
      const errorParams: { errorCode: string; assessment?: string } = JSON.parse(
        gpt4response.choices[0].message.function_call.arguments,
      )
      throw new AiBrandVoiceDetectionError(errorParams)
    }

    // TODO: Migrate the errors in brand voice detection to use tool calls
    if (gpt4response.choices[0].finish_reason === 'tool_calls') {
      throw new AiBrandVoiceDetectionError({ errorCode: 'other' })
    }

    const jsonObjects = gpt4response.choices[0].message.content.match(/{[^{}]*}/g)

    if (!jsonObjects || jsonObjects.length < 1) {
      throw new AiMalformedResponseError('Malformed response', gpt4response)
    }

    return JSON.parse(jsonObjects[0]) as VoiceParams
  },
}
